import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import { Popover } from 'react-native-popper'

import styled, { useTheme } from 'styled-components/native'

import { DocumentFiltersPopover, FilterSectionConfig } from './DocumentFiltersPopover'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { FilterIcon } from '../../atoms/icons/FilterIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { TextButton } from '../../atoms/textButton/TextButton'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
  alignItems: 'center',
}))

const Button = styled(PressableOpacity)(({ theme }) => ({
  padding: theme.spacing['4px'],
  alignSelf: 'flex-start',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const ActiveFilterCount = styled(BodyText)(({ theme }) => ({
  width: '24px',
  height: '24px',
  backgroundColor: theme.colors.backgroundActive,
  borderRadius: '4px',
  textAlign: 'center',
  lineHeight: '24px',
}))

const ClearFiltersButton = styled(TextButton)({
  alignSelf: 'center',
})

export * from './DocumentFiltersPopover'

export type DocumentFiltersProps<T> = {
  filterState: T
  initialFilterState: T
  onFilterStateChange: (filterState: T) => void
  sections: FilterSectionConfig<T>[]
  renderTrigger?: (onPress: () => void, activeFilters: number) => React.ReactNode
}

export const DocumentFilters = <T,>({
  filterState,
  initialFilterState,
  onFilterStateChange,
  sections,
  renderTrigger,
}: DocumentFiltersProps<T>) => {
  const ref = useRef(null)
  const { colors } = useTheme()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const activeFilters = useMemo(
    () => Object.values(filterState as Record<string, unknown[]>).filter((arr) => arr.length > 0).length,
    [filterState],
  )

  return (
    <Container>
      <Popover
        trigger={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement='bottom left'
        animated={!window.Cypress}
      >
        <Popover.Content>
          {(!window.Cypress || isPopoverOpen) && (
            <DocumentFiltersPopover
              filterState={filterState}
              onFilterStateChange={onFilterStateChange}
              sections={sections}
            />
          )}
        </Popover.Content>
      </Popover>
      {renderTrigger ? (
        <View ref={ref}>{renderTrigger(() => setIsPopoverOpen((prev) => !prev), activeFilters)}</View>
      ) : (
        <Button
          testID={tID('DocumentFilters-filter-button')}
          pressableRef={ref}
          onPress={() => setIsPopoverOpen((prev) => !prev)}
        >
          <FilterIcon size={24} color={colors.iconDefault} />
          {activeFilters > 0 && (
            <ActiveFilterCount
              testID={tID('DocumentFilters-active-filter-count')}
              text={activeFilters}
              size={BodyTextSize.BADGE}
              color={colors.textInverse}
            />
          )}
        </Button>
      )}
      {!renderTrigger && activeFilters > 0 && (
        <ClearFiltersButton
          testID={tID('DocumentFilters-clear-filters-button')}
          leftIcon={<XIcon fillColor={colors.iconActive} size={24} />}
          text={<FormattedMessage defaultMessage='Clear filters' description='Clear filters button' />}
          onPress={() => onFilterStateChange(initialFilterState)}
        />
      )}
    </Container>
  )
}
